import { useEffect, useState } from 'react'

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  InputAdornment,
} from '@mui/material'
import { Search } from 'lucide-react'

import { PaginationFooter, StatusBadge, TruncatedCell } from '../../../common/components'
import { formatDateOnly } from '../../../common/functions/timeConversion'
import { useLanguage } from '../../../i18n'
import { useTimezone } from '../../../utils/datetime'

import { getAssetTagSwapHistory } from './api'

import { useDebouncedValue } from '@/hooks'

const STATUS_COLOR = {
  COUPLE: 'green',
  DECOUPLE: 'red',
  SWAP: 'blue',
}

const PLACEHOLDER = '—'

// Column `id`s for sortable headers match the backend field names so the
// sort key flows straight through `getAssetTagSwapHistory` without a
// translation table: sorting by "Date" → `orderBy=performedAt`, sorting
// by "Status" → `orderBy=actionType`. Non-sortable columns are pinned
// (no sort affordance) because the swap-history endpoint can't honor a
// sort by reason / by / device serial in a meaningful way.
const historyColumns = [
  { id: 'performedAt', label: 'Date', sortable: true },
  { id: 'actionType', label: 'Status', sortable: true },
  { id: 'previousDeviceId', label: 'Previous device', sortable: false },
  { id: 'newDeviceId', label: 'New device', sortable: false },
  { id: 'reasonNotes', label: 'Reason', sortable: false },
  { id: 'performedByUserId', label: 'By', sortable: false },
]

function AssetTagHistoryTable({ uuid }) {
  // Re-render this page when the admin changes the Global Settings
  // timezone, so every timestamp below re-formats immediately.
  useTimezone()
  const [order, setOrder] = useState('desc')
  const [orderBy, setOrderBy] = useState('performedAt')
  const [page, setPage] = useState(1)
  const [history, setHistory] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [search, setSearch] = useState('')

  const { t } = useLanguage()

  // The history endpoint is keyed on the asset-tag uuid, which arrives
  // asynchronously via the device query in DeviceDetail. Until it does
  // we must not mount PaginationFooter — otherwise React Query fires a
  // request whose URL contains the literal string "undefined".
  const ready = Boolean(uuid)

  // Throttle the search input so we only fire one backend request per pause
  // in typing instead of one per keystroke.
  const debouncedSearch = useDebouncedValue(search, 300)

  // Reset to the first page whenever the search input changes — otherwise
  // a stale page index can land past the end of the new result set.
  useEffect(() => {
    setPage(1)
  }, [debouncedSearch])

  const handleSort = (col) => {
    const isAsc = orderBy === col && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(col)
  }

  // Page + sort + search + uuid are forwarded to PaginationFooter, which
  // runs the useQuery and writes the response array into `setListData`.
  // `uuid` lives in `params` because PaginationFooter reads it from there
  // and includes it in both the request and the cache key. Sort is sent
  // as `orderBy` / `order` (the keys `getAssetTagSwapHistory` reads and
  // forwards as query-string params on GET /asset-tags/swap-history/{uuid})
  // — using the legacy `sortColumn` / `sortDirection` keys would silently
  // drop the sort.
  const paginationParams = {
    uuid,
    currentPage: page,
    setCurrentPage: setPage,
    orderBy,
    order,
    search: debouncedSearch,
  }

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder={t('Search history...')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 240 }}
        />
        {/* <Button
          variant="outlined"
          startIcon={<SlidersHorizontal size={16} />}
          sx={{ textTransform: 'none', ml: 'auto' }}
        >
          Filter
        </Button> */}
      </Box>
      <TableContainer sx={{ maxHeight: 480, width: '100%', overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 720 }} stickyHeader>
          <TableHead>
            <TableRow>
              {historyColumns.map((col) => (
                <TableCell key={col.id}>
                  {col.sortable ? (
                    <TableSortLabel
                      active={orderBy === col.id}
                      direction={orderBy === col.id ? order : 'asc'}
                      onClick={() => handleSort(col.id)}
                    >
                      <TruncatedCell>{col.label}</TruncatedCell>
                    </TableSortLabel>
                  ) : (
                    <TruncatedCell>{col.label}</TruncatedCell>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!ready && (
              <TableRow>
                <TableCell colSpan={historyColumns.length} align="center" sx={{ py: 4 }}>
                  {t('No asset tag associated with this device')}
                </TableCell>
              </TableRow>
            )}
            {ready && isLoading && history.length === 0 && (
              <TableRow>
                <TableCell colSpan={historyColumns.length} align="center" sx={{ py: 4 }}>
                  {t('Loading history…')}
                </TableCell>
              </TableRow>
            )}
            {ready &&
              !isLoading &&
              history.map((row, i) => (
                <TableRow key={i}>
                  {/* performedAt → date */}
                  <TableCell className="text-nowrap">
                    <TruncatedCell>{formatDateOnly(row.performedAt)}</TruncatedCell>
                  </TableCell>
                  {/* actionType → status badge */}
                  <TableCell>
                    <StatusBadge color={STATUS_COLOR[row.actionType] ?? 'default'} dot>
                      {row.actionType}
                    </StatusBadge>
                  </TableCell>
                  {/* previousDeviceId → previous device */}
                  <TableCell>
                    <TruncatedCell>{row.previousDevice?.serialNumber ?? PLACEHOLDER}</TruncatedCell>
                  </TableCell>
                  {/* newDeviceId → new device */}
                  <TableCell>
                    <TruncatedCell>{row?.newDevice?.serialNumber ?? PLACEHOLDER}</TruncatedCell>
                  </TableCell>
                  {/* reasonNotes → reason */}
                  <TableCell>
                    <TruncatedCell>{row.reasonNotes ?? PLACEHOLDER}</TruncatedCell>
                  </TableCell>
                  {/* performedByUserId → by */}
                  <TableCell>
                    <TruncatedCell>{row.performedByUserId ?? PLACEHOLDER}</TruncatedCell>
                  </TableCell>
                </TableRow>
              ))}
            {ready && !isLoading && history.length === 0 && (
              <TableRow>
                <TableCell colSpan={historyColumns.length} align="center" sx={{ py: 4 }}>
                  {t('No history found')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {ready && (
        <PaginationFooter
          listCall={(p) => getAssetTagSwapHistory({ ...p, uuid })}
          listCallMode="Object"
          params={paginationParams}
          setListData={setHistory}
          setIsLoading={setIsLoading}
          querykey="asset-tag-swap-history"
        />
      )}
    </>
  )
}

export default AssetTagHistoryTable
